import React, { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Bot, Send, ShoppingBag, Euro, Star, TrendingUp, Sparkles, Lightbulb, UtensilsCrossed, Tag, DollarSign, Clock, MessageSquarePlus, Trophy, CalendarDays } from "lucide-react";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Qual è il mio piatto più venduto?",
  "Come posso aumentare gli ordini questa settimana?",
  "Quali prodotti non vende nessuno?",
  "Che promozione mi consigli?",
];

const CAT_ICON = { menu: UtensilsCrossed, promo: Tag, pricing: DollarSign, servizio: Star };

function Kpi({ icon: Icon, label, value, color = "#00E676" }) {
  return (
    <div className="bg-[#1C1C1F] border border-white/10 rounded-xl p-4" data-testid={`biz-kpi-${label}`}>
      <div className="flex items-center gap-2 text-zinc-400 text-xs mb-1"><Icon className="h-4 w-4" style={{ color }} /> {label}</div>
      <div className="text-2xl font-display font-bold" style={{ color }}>{value}</div>
    </div>
  );
}

function priorityColor(p) {
  if (p === "alta") return "text-red-300 border-red-400/30 bg-red-400/10";
  if (p === "media") return "text-amber-300 border-amber-400/30 bg-amber-400/10";
  return "text-zinc-300 border-white/15";
}

function MiniChart({ series, dataKey, label }) {
  const max = Math.max(1, ...series.map((s) => s[dataKey] || 0));
  return (
    <div>
      <div className="flex items-end gap-1.5 h-24">
        {series.map((s, i) => (
          <div key={i} className="flex-1 flex flex-col items-center gap-1" title={`${label}: ${s[dataKey]}`}>
            <div className="w-full rounded-t bg-[#00E676]/70 transition-all" style={{ height: `${Math.max(4, (s[dataKey] / max) * 84)}px` }} />
            <span className="text-[9px] text-zinc-500">{new Date(s.date).toLocaleDateString("it-IT", { weekday: "short" }).slice(0, 2)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function BusinessAssistant() {
  const [kpis, setKpis] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [insights, setInsights] = useState(null);
  const [loadingIns, setLoadingIns] = useState(false);
  const [sessionId, setSessionId] = useState("biz-default");
  const endRef = useRef(null);

  const loadKpis = () => api.get("/business/assistant/kpis").then(({ data }) => setKpis(data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  const loadHistory = (sid) => api.get("/business/assistant/history", { params: { session_id: sid } }).then(({ data }) => setMessages(data.map((m) => ({ role: m.role, text: m.text }))));

  useEffect(() => { loadKpis(); loadHistory(sessionId); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: q }]);
    setBusy(true);
    try {
      const { data } = await api.post("/business/assistant", { message: q, session_id: sessionId }, { timeout: 60000 });
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
      if (data.kpis) setKpis(data.kpis);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Assistente non disponibile");
      setMessages((m) => [...m, { role: "assistant", text: "Mi dispiace, al momento non riesco a rispondere. Riprova tra poco." }]);
    } finally { setBusy(false); }
  };

  const newChat = () => { const sid = `biz-${Date.now()}`; setSessionId(sid); setMessages([]); toast.success("Nuova conversazione"); };

  const genInsights = async () => {
    setLoadingIns(true);
    try {
      const { data } = await api.post("/business/assistant/insights", {}, { timeout: 60000 });
      setInsights(data.insights || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Impossibile generare i suggerimenti");
    } finally { setLoadingIns(false); }
  };

  if (!kpis) return <div className="text-zinc-400">Caricamento assistente…</div>;

  return (
    <div className="space-y-6" data-testid="business-assistant">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-[#00E676]/15 border border-[#00E676]/30 flex items-center justify-center"><Bot className="h-5 w-5 text-[#00E676]" /></div>
        <div>
          <h2 className="font-display text-xl font-bold">Assistente IA · {kpis.business_name}</h2>
          <p className="text-sm text-zinc-400">Analizza i tuoi dati reali e ti consiglia su menu, promo e prezzi. Sola lettura.</p>
        </div>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={ShoppingBag} label="Ordini oggi" value={kpis.orders_today} />
        <Kpi icon={Euro} label="Ricavi 7gg" value={`€${kpis.revenue_week}`} />
        <Kpi icon={TrendingUp} label="Ticket medio" value={`€${kpis.avg_ticket}`} />
        <Kpi icon={Star} label="Valutazione" value={kpis.avg_rating ? `${kpis.avg_rating}★` : "—"} color="#F59E0B" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Andamento + previsione */}
        <div className="bg-[#1C1C1F] border border-white/10 rounded-xl p-5">
          <h3 className="font-semibold mb-3 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-[#00E676]" /> Ordini ultimi 7 giorni</h3>
          <MiniChart series={kpis.series_7d} dataKey="orders" label="Ordini" />
          <div className="mt-3 text-xs text-zinc-500 flex flex-wrap gap-3">
            {kpis.busiest_weekday && <span className="flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> Giorno di punta: <span className="text-zinc-300">{kpis.busiest_weekday}</span></span>}
            {kpis.busiest_hour && <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> Orario di punta: <span className="text-zinc-300">{kpis.busiest_hour}</span></span>}
          </div>
        </div>

        {/* Previsione ordini */}
        <div className="bg-[#1C1C1F] border border-white/10 rounded-xl p-5" data-testid="biz-forecast">
          <h3 className="font-semibold mb-3 flex items-center gap-2"><Sparkles className="h-4 w-4 text-[#00E676]" /> Previsione prossimi 7 giorni</h3>
          <p className="text-[11px] text-zinc-500 mb-2">Stima basata sulla media storica per giorno della settimana.</p>
          <div className="space-y-1.5">
            {kpis.forecast_next_7d.map((f, i) => {
              const n = Math.round(f.predicted_orders);
              return (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="text-zinc-400 capitalize">{f.weekday} {new Date(f.date).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" })}</span>
                  <span className="text-[#00E676] font-semibold">~{n} {n === 1 ? "ordine" : "ordini"}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top / zero-sales */}
        <div className="bg-[#1C1C1F] border border-white/10 rounded-xl p-5">
          <h3 className="font-semibold mb-2 flex items-center gap-2"><Trophy className="h-4 w-4 text-[#F59E0B]" /> Prodotti più venduti</h3>
          {kpis.top_products.length === 0 ? <div className="text-sm text-zinc-500">Ancora nessuna vendita.</div> : (
            <div className="space-y-1">{kpis.top_products.map((p, i) => (
              <div key={i} className="flex justify-between text-sm"><span className="truncate mr-2">{p.name}</span><span className="text-[#00E676] whitespace-nowrap">{p.qty}× · €{p.revenue}</span></div>
            ))}</div>
          )}
          {kpis.zero_sales_products.length > 0 && (
            <div className="mt-3">
              <div className="text-xs text-zinc-500 uppercase tracking-widest mb-1">Mai ordinati</div>
              <div className="text-xs text-zinc-400">{kpis.zero_sales_products.map((p) => p.name).join(" · ")}</div>
            </div>
          )}
        </div>
      </div>

      {/* Suggerimenti strategici */}
      <div className="bg-gradient-to-br from-[#101014] to-[#16161b] border border-white/10 rounded-xl p-5" data-testid="biz-insights">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <h3 className="font-semibold flex items-center gap-2"><Lightbulb className="h-4 w-4 text-[#00E676]" /> Suggerimenti su menu, promo e prezzi</h3>
          <Button size="sm" onClick={genInsights} disabled={loadingIns} className="bg-[#00E676] text-black" data-testid="biz-gen-insights">
            {loadingIns ? "Genero…" : "Genera suggerimenti"}
          </Button>
        </div>
        {!insights && <p className="text-sm text-zinc-500">Premi «Genera suggerimenti» per ricevere consigli pratici basati sui tuoi dati reali.</p>}
        {insights && insights.length === 0 && <p className="text-sm text-zinc-500">Nessun suggerimento disponibile al momento.</p>}
        {insights && insights.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {insights.map((ins, i) => {
              const Icon = CAT_ICON[ins.category] || Lightbulb;
              return (
                <div key={i} className={`border rounded-lg p-3 ${priorityColor(ins.priority)}`} data-testid={`biz-insight-${i}`}>
                  <div className="flex items-center gap-2 font-semibold text-sm"><Icon className="h-4 w-4" /> {ins.title}</div>
                  <div className="text-sm text-zinc-300 mt-1">{ins.detail}</div>
                  {ins.metric && <div className="text-[11px] text-zinc-500 mt-1">Dato: {ins.metric}</div>}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Chat */}
      <div className="bg-[#1C1C1F] border border-white/10 rounded-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold flex items-center gap-2"><Bot className="h-4 w-4 text-[#00E676]" /> Chiedi all'assistente</h3>
          <Button size="sm" variant="secondary" onClick={newChat} data-testid="biz-new-chat"><MessageSquarePlus className="h-4 w-4 mr-1" /> Nuova chat</Button>
        </div>
        <div className="flex flex-wrap gap-2 mb-3">
          {SUGGESTIONS.map((s, i) => <button key={i} onClick={() => send(s)} className="text-xs px-3 py-1.5 rounded-full border border-white/10 text-zinc-300 hover:border-[#00E676]/50 hover:text-white transition-colors" data-testid={`biz-suggestion-${i}`}>{s}</button>)}
        </div>
        <div className="space-y-3 max-h-96 overflow-y-auto mb-3 pr-1" data-testid="biz-chat-messages">
          {messages.length === 0 && <div className="text-sm text-zinc-500 text-center py-6">Fammi una domanda sui tuoi ordini, prodotti o su come crescere. 🚀</div>}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-[#00E676] text-black" : "bg-white/5 text-zinc-100 border border-white/10"}`}>{m.text}</div>
            </div>
          ))}
          {busy && <div className="flex justify-start"><div className="bg-white/5 border border-white/10 rounded-2xl px-4 py-2 text-sm text-zinc-400">Sto analizzando i tuoi dati…</div></div>}
          <div ref={endRef} />
        </div>
        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2">
          <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Scrivi la tua domanda…" className="bg-[#0A0A0C] border-white/10" data-testid="biz-chat-input" />
          <Button type="submit" disabled={busy} className="bg-[#00E676] text-black" data-testid="biz-chat-send"><Send className="h-4 w-4" /></Button>
        </form>
      </div>
    </div>
  );
}
